import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useTrip } from "@/lib/store";
import { SubHeader } from "../More";

export default function Costs() {
  const { trip, organiser, setOrganiser, updateExpenses } = useTrip();
  const [clar, setClar] = useState(trip.expenses.clarification || "");
  const sym = trip.settings.currencySymbol || "$";
  const money = (n) => `${sym}${Number(n).toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const hotelTotal = trip.expenses.hotels.reduce((s, h) => s + h.total, 0);

  if (!organiser) return (
    <div className="pb-8"><SubHeader eyebrow="Costs" title="Organiser view is off" />
      <button data-testid="enable-organiser-btn" onClick={() => setOrganiser(true)} className="tap mt-4 rounded-full bg-[var(--plum)] px-5 py-3 text-sm font-semibold text-white">Turn on Organiser view</button></div>
  );

  return (
    <div className="pb-8">
      <SubHeader eyebrow="Costs" title="Money, as written" />
      <p className="mt-2 text-xs text-[var(--ink-muted)]">Amounts are exactly as stated in the spreadsheet. The source uses “{sym}” without naming a currency{trip.settings.currencyCode ? ` — organiser has set ${trip.settings.currencyCode}` : " — choose one in Settings if known"}.</p>

      <section className="card-soft mt-4 overflow-hidden">
        <div className="flex items-center justify-between bg-[var(--jade)] px-4 py-3 text-[var(--parchment)]"><p className="eyebrow !text-[var(--brass)]">Hotels</p><p className="font-mono text-sm">{money(hotelTotal)}</p></div>
        <ul className="divide-y">
          {trip.expenses.hotels.map((h, i) => {
            const hotel = trip.hotels.find((x) => x.id === h.hotelId);
            return (
              <li key={i} data-testid={`cost-hotel-${i}`} className="flex items-start justify-between gap-3 px-4 py-3 text-sm">
                <div><p className="font-semibold">{h.label}</p><p className="text-xs text-[var(--ink-muted)]">{h.rooms}{h.eachNote ? ` · ${h.eachNote}` : ""}</p>
                  {hotel?.paymentStatus === "unpaid" && <p className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-rose-700"><AlertTriangle className="h-3 w-3" /> Not paid per spreadsheet</p>}</div>
                <p className="font-mono">{money(h.total)}</p>
              </li>
            );
          })}
        </ul>
      </section>

      <section className="card-soft mt-4 overflow-hidden">
        <div className="bg-[var(--plum)] px-4 py-3 text-white"><p className="eyebrow !text-white/80">Amounts owed — as written in source</p></div>
        <ul className="divide-y">
          {trip.expenses.owed.map((o) => (
            <li key={o.person} data-testid={`owed-${o.person.toLowerCase()}`} className="flex items-start justify-between gap-3 px-4 py-3 text-sm">
              <div><p className="font-semibold">{o.person}</p><p className="text-xs italic text-[var(--ink-muted)]">“{o.sourceNote}”</p></div>
              <p className="font-mono">{money(o.amount)}</p>
            </li>
          ))}
          {trip.expenses.other.map((o) => (
            <li key={o.label} data-testid="cost-other" className="flex items-start justify-between gap-3 px-4 py-3 text-sm">
              <div><p className="font-semibold">{o.label}</p><p className="text-xs italic text-[var(--ink-muted)]">“{o.note}”</p></div>
              <p className="font-mono">{money(o.amount)}</p>
            </li>
          ))}
        </ul>
        <div className="border-t bg-[var(--parchment-2)] p-4">
          <p className="eyebrow">Clarification (edit freely)</p>
          <p className="mt-1 text-xs text-[var(--ink-muted)]">The meaning of “excluding 1” and “extra thing” isn't defined in the source. Record what it means here.</p>
          <textarea data-testid="clarification-input" rows={3} value={clar} onChange={(e) => setClar(e.target.value)} className="mt-2 w-full rounded-xl border bg-white p-3 text-sm" placeholder="e.g. “excluding 1” refers to…" />
          <button data-testid="clarification-save" onClick={() => { updateExpenses({ clarification: clar }); toast.success("Saved"); }} className="tap mt-2 rounded-full bg-[var(--jade)] px-4 py-2 text-xs font-semibold text-[var(--parchment)]">Save</button>
        </div>
      </section>
    </div>
  );
}
